import { Component, computed, input, output } from '@angular/core';

import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';

export interface MergeDataItem {
  id: number | string;
  primary: string;
  secondary?: string;
}

export interface MergeSelectionState {
  itemsToAdd: Set<number | string>;
  itemsToRemove: Set<number | string>;
}

export interface MergeSelectionChangeEvent {
  type: 'add' | 'remove';
  id: number | string;
  selected: boolean;
}

@Component({
  selector: 'xm-clinical-data-merge-section',
  standalone: true,
  imports: [
    MatCardModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule
  ],
  templateUrl: './clinical-data-merge-section.component.html',
  styleUrls: ['./clinical-data-merge-section.component.scss']
})
export class ClinicalDataMergeSectionComponent {
  // Display configuration
  title = input.required<string>();
  icon = input<string>('list');
  iconColor = input<'primary' | 'accent' | 'warn'>('primary');
  itemLabel = input<string>('items');

  // Data
  existingItems = input<MergeDataItem[]>([]);
  referralItems = input<MergeDataItem[]>([]);
  existingItemIds = input<Set<number | string>>(new Set());

  // Selection state
  itemsToAdd = input<Set<number | string>>(new Set());
  itemsToRemove = input<Set<number | string>>(new Set());

  // Outputs
  selectionChange = output<MergeSelectionChangeEvent>();

  // Computed: Items that are in our patient but NOT in referral (candidates for removal)
  itemsToRemoveList = computed<MergeDataItem[]>(() => {
    const referralItemNames = new Set(
      this.referralItems().map(item => item.primary.toLowerCase().trim())
    );
    return this.existingItems().filter(
      item => !referralItemNames.has(item.primary.toLowerCase().trim())
    );
  });

  // Computed: Items that exist in both patient and referral
  existingMatchItems = computed<MergeDataItem[]>(() => {
    return this.referralItems().filter(item => this.existingItemIds().has(item.id));
  });

  // Computed: New items from referral (not in existing patient)
  newReferralItems = computed<MergeDataItem[]>(() => {
    return this.referralItems().filter(item => !this.existingItemIds().has(item.id));
  });

  // Check if referral item already exists in patient record
  isExistingItem(id: number | string): boolean {
    return this.existingItemIds().has(id);
  }

  // Check if item is marked for removal
  isMarkedForRemoval(id: number | string): boolean {
    return this.itemsToRemove().has(id);
  }

  // Check if item is marked for addition
  isMarkedForAddition(id: number | string): boolean {
    return this.itemsToAdd().has(id);
  }

  // Toggle existing item (unchecked = remove)
  onExistingItemToggle(id: number | string, checked: boolean): void {
    // If unchecked, mark for removal; if checked, remove from removal list
    this.selectionChange.emit({
      type: 'remove',
      id,
      selected: !checked // selected=true means "mark for removal"
    });
  }

  // Toggle referral item (checked = add)
  onReferralItemToggle(id: number | string, checked: boolean): void {
    this.selectionChange.emit({
      type: 'add',
      id,
      selected: checked
    });
  }
}
